import { type ReactNode, useState } from 'react'
import { cn } from 'app/lib/utils'
import type { ListBoardTone, ListBoardWidth } from './boardTypes'

// Both maps hold classes moved verbatim from the two boards this lane replaced. G1
// changes no pixels, so the arbitrary widths and the raw greens stay until the boards'
// look is converged on purpose.
const WIDTH_CLASS: Readonly<Record<ListBoardWidth, string>> = {
  regular: `min-w-[280px]`,
  wide: `min-w-[322px]`,
}

const OVER_CLASS: Readonly<Record<ListBoardTone, string>> = {
  neutral: `border-strong bg-surface-muted`,
  green: `border-green-400 bg-green-50/60`,
}

export function ListBoardLane({
  laneKey,
  label,
  labelAdornment,
  selectControl,
  count,
  headerExtra,
  headerAction,
  droppable = true,
  isEmpty,
  emptyLabel,
  footer,
  tone,
  width,
  solo = false,
  touchDrop,
  touchOver,
  draggingId,
  onDrop,
  children,
}: {
  laneKey: string
  label: string
  /** Before the label, such as a stage's color dot. */
  labelAdornment?: ReactNode
  /** First in the label row while the board is selecting: picks every card in the lane. */
  selectControl?: ReactNode
  count: ReactNode
  headerExtra?: ReactNode
  /** At the right end of the label row, after the count, such as a stage menu. */
  headerAction?: ReactNode
  /** False for a lane cards can leave but never enter. Defaults to true. */
  droppable?: boolean
  isEmpty: boolean
  emptyLabel?: string
  footer?: ReactNode
  tone: ListBoardTone
  width: ListBoardWidth
  /** The board's only lane. It stops growing at a column's width instead of spanning the board. */
  solo?: boolean
  /** Whether a finger drag can land here (see `useTouchCardDrag`). */
  touchDrop: boolean
  touchOver?: boolean
  draggingId?: string
  /** The dropped card's id, or undefined when the drop carried none and nothing was in the air. */
  onDrop: (itemId: string | undefined) => void
  children: ReactNode
}) {
  const [dragOver, setDragOver] = useState(false)
  const over = dragOver || !!touchOver

  return (
    <div
      data-board-drop={touchDrop && droppable ? laneKey : undefined}
      onDragOver={
        droppable
          ? (event) => {
              event.preventDefault()
              setDragOver(true)
            }
          : undefined
      }
      onDragLeave={droppable ? () => setDragOver(false) : undefined}
      onDrop={
        droppable
          ? (event) => {
              setDragOver(false)
              onDrop(event.dataTransfer.getData(`text/plain`) || draggingId)
            }
          : undefined
      }
      className={cn(
        `flex h-full flex-1 flex-col overflow-hidden rounded-2xl border p-3 transition`,
        WIDTH_CLASS[width],
        solo && `md:max-w-sm`,
        // On a phone a lane is exactly the board's width, one lane per swipe, so it lines up
        // with the drop targets beneath it instead of overhanging them.
        `max-md:min-w-full`,
        over ? OVER_CLASS[tone] : `border-default/70 bg-surface-subtle/60`,
      )}
    >
      <div className="shrink-0 px-1">
        <div className="flex items-baseline justify-between gap-2">
          {selectControl}
          {labelAdornment}
          <p
            className={cn(
              `text-ui-sm text-strong`,
              (labelAdornment || headerAction || selectControl) && `min-w-0 grow truncate`,
            )}
          >
            {label}
          </p>
          {count}
          {headerAction}
        </div>
        {headerExtra}
      </div>

      {/* The lane scrolls, not the page. The negative margins park the scrollbar and the
          cards' hover shadow in the lane's own gutter instead of clipping either. The gutter
          is `stable` because the scrollbar's width is the cards' only right inset: a lane
          short enough not to scroll otherwise runs its cards into the lane's border. */}
      <div className="soft-scrollbar -ml-1.5 -mr-3 mt-1.5 flex min-h-0 flex-1 flex-col gap-2.5 scrollbar-gutter-stable overflow-y-auto pb-5 pl-1.5 pt-1.5">
        {children}
        {isEmpty && emptyLabel !== undefined && (
          <p className="rounded-xl border border-dashed border-default px-3 py-6 text-center text-body-xs text-minimal">
            {over ? `Drop here` : emptyLabel}
          </p>
        )}
      </div>

      {footer}
    </div>
  )
}
